import { shouldShowDisconnectedAccountBanner } from './disconnected-account-banner'
import { GNotice } from 'components/basic-blocks/g-notice'
import { GNoticeType } from 'components/basic-blocks/g-notice/types'
import {
	buildCancellationNotice,
	buildCompletedNotice,
	buildInActiveNotice,
	buildInSetupDemoDataNotice,
	buildInSetupTrackingCodeNotice,
	buildMccLinkRequestNotice
} from 'components/basic-blocks/g-notice/utils'
import { InactiveTrackingCodeBanner } from 'components/basic-blocks/g-notice/notices/inactive-tracking-code-banner'
import { BuildDashboardNoticesProps } from './types'
import { buildWarningNotices } from './utils'
import { useAccountStore, useAssetStore } from 'store'
import { useShallow } from 'zustand/react/shallow'
import { useState } from 'react'
import { MccAcceptInstructionsModal } from 'components/mcc-link-connection/mcc-accept-instructions-modal'
import { useWorkspaceAccess } from 'hooks/use-workspace-access'
import { PlanEndedBanner } from './plan-ended-banner'

export const Notices = ({
	isInSetup,
	setupStep,
	tc_installed,
	tc_installed_skipped,
	cc_installed,
	hasDashboardPerformanceFeature,
	accountId,
	assetId,
	demoMode,
	hideDemoDataNotice = false,
	isInActive,
	mccLinkInviteAdAccounts = []
}: BuildDashboardNoticesProps) => {
	const { account } = useAccountStore(
		useShallow((s) => ({ account: s.account }))
	)
	const { adAccounts, asset } = useAssetStore(
		useShallow((s) => ({
			adAccounts: s.adAccounts,
			asset: s.asset
		}))
	)
	const hasAdAccountInAsset = adAccounts.length > 0
	const setupFlowV2Enabled = true
	const { access } = useWorkspaceAccess()

	// DEV-2353 — the one-time accept-instructions modal is hosted here (a stable parent)
	// rather than inside the banner, because sending the invite changes the account state
	// and unmounts the banner immediately after the send.
	const [acceptModalOpen, setAcceptModalOpen] = useState(false)
	if (access === 'not-started' || accountId === 'workspace-demo') return null

	// Issue 6C — suppress the banner while a workspace / website switch is in
	// flight. Route changes update `assetId`/`accountId` props immediately, but
	// the store-driven `asset`/`account` lag for ~1s until the switch resolves.
	// Without this guard, the banner renders the previous workspace's variant
	// (e.g. "Action required: tracking code not detected on <prev-site>") for
	// a beat before flipping to the correct one for the new selection.
	const isAccountStale =
		!!accountId &&
		accountId !== 'workspace-demo' &&
		!!account &&
		account.id !== accountId
	const isAssetStale =
		!!assetId &&
		assetId !== 'website-demo' &&
		!!asset &&
		asset.id !== assetId
	if (isAccountStale || isAssetStale) return null

	let notices: GNoticeType[]

	if (isInSetup || setupStep !== 'SETUP_COMPLETE') {
		notices = [
			...buildInSetupDemoDataNotice({
				step: setupStep,
				accountId,
				account: account || undefined,
				assetId,
				setupFlowV2Enabled
			})
		]
	} else {
		const contextKey = `${assetId || 'website-demo'}-dashboard`

		notices = [
			...buildWarningNotices({
				tc_installed,
				cc_installed,
				hasDashboardPerformanceFeature,
				accountId,
				assetId: assetId || 'website-demo',
				contextKey
			})
		]
	}
	if (!tc_installed && !demoMode) {
		notices = [
			...buildInSetupDemoDataNotice({
				step: setupStep,
				accountId,
				account: account || undefined,
				assetId,
				setupFlowV2Enabled
			})
		]
	}
	if (setupStep === 'SETUP_COMPLETE' && tc_installed && !demoMode) {
		notices = [...buildCompletedNotice()]
	}
	// Inactive tracking code banner: active account & asset with TC not detected post-setup
	// Takes priority over dismissable warning notices but NOT over critical notices (inactive/cancelling)
	if (
		!isInActive &&
		!tc_installed &&
		setupStep === 'SETUP_COMPLETE' &&
		account?.status === 'ACTIVE' &&
		asset?.status === 'ACTIVE'
	) {
		notices = [
			{
				key: 'inactive-tracking-code',
				hideDismiss: true,
				containerClassName: '!font-[500] !rounded-[12px] !border-t-border-lighter',
				notice: (
					<InactiveTrackingCodeBanner
						websiteName={asset?.name || ''}
						accountId={accountId || ''}
						assetId={assetId || ''}
					/>
				)
			}
		]
	}

	if (
		setupStep === 'SETUP_COMPLETE' &&
		!hasAdAccountInAsset &&
		account &&
		assetId
	) {
		notices = [
			...buildInSetupTrackingCodeNotice({ account, assetId, websiteStep: '2' })
		]
	} else if (
		setupStep === 'SETUP_COMPLETE' &&
		hasAdAccountInAsset &&
		!tc_installed &&
		!tc_installed_skipped &&
		account &&
		assetId
	) {
		notices = [
			{
				key: 'inactive-tracking-code',
				hideDismiss: true,
				containerClassName: '!font-[500] !rounded-[12px] !border-t-border-lighter',
				notice: (
					<InactiveTrackingCodeBanner
						websiteName={asset?.name || ''}
						accountId={accountId || ''}
						assetId={assetId || ''}
					/>
				)
			}
		]
	} else if (
		setupStep === 'SETUP_COMPLETE' &&
		mccLinkInviteAdAccounts?.length &&
		tc_installed
	) {
		notices = [
			...buildMccLinkRequestNotice(mccLinkInviteAdAccounts, accountId, () =>
				setAcceptModalOpen(true)
			)
		]
	}

	// Critical notices last, so no setup or MCC banner can replace them.
	if (isInActive) {
		notices = [...buildInActiveNotice({ accountId })]
	}
	if (account?.billing?.subscription?.status === 'CANCELLING') {
		notices = [...buildCancellationNotice()]
	}

	notices = hideDemoDataNotice
		? notices.filter(
				(item) => item.key !== 'in-setup-last-step' && item.key !== 'in-setup'
		  )
		: notices

	if (
		shouldShowDisconnectedAccountBanner(account, asset, adAccounts, demoMode)
	) {
		notices = []
	}

	// Note: don't early-return on empty notices — the accept-instructions modal must stay
	// mounted after the banner that triggered it disappears.
	return (
		<>
			{/* Case 6 (cancelled): the DS danger banner replaces the inactive one. */}
			{access === 'cancelled' && notices.some((n) => n.key === 'in-active') ? (
				<PlanEndedBanner />
			) : (
				notices.length > 0 && (
					<div>
						<GNotice notices={notices} />
					</div>
				)
			)}
			<MccAcceptInstructionsModal
				open={acceptModalOpen}
				onClose={() => setAcceptModalOpen(false)}
			/>
		</>
	)
}
